import { useMemo, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import Select, { Option } from "../../../components/Select";
import { Toggle } from "../../../components/Toggle";
import { SubjectTypeIcon } from "../../../components/SubjectTypeIcon";

export type GraphDirection = "outbound" | "inbound" | "both";

/** filters that change which graph is requested, so changing one refetches */
export type LoadFilters = {
  /** null is every level, and omits the parameter entirely */
  depth: number | null;
  direction: GraphDirection;
  /** relationship type codes */
  relationshipTypes: string[];
};

/** filters applied to the graph already loaded, so they cost no request */
export type ShowFilters = {
  /** empty means no restriction */
  profileTypes: string[];
  /** empty means no restriction */
  profileStatuses: string[];
  includeEnded: boolean;
};

export const DEFAULT_LOAD_FILTERS: LoadFilters = {
  depth: null,
  direction: "both",
  relationshipTypes: [],
};

export const DEFAULT_SHOW_FILTERS: ShowFilters = {
  profileTypes: [],
  profileStatuses: [],
  // ended relationships are shown by default: hiding them would conceal the
  // history someone reviewing a structure needs to see
  includeEnded: true,
};

export const hasActiveLoadFilters = (filters: LoadFilters) =>
  filters.depth !== null ||
  filters.direction !== "both" ||
  filters.relationshipTypes.length > 0;

export const hasActiveShowFilters = (filters: ShowFilters) =>
  filters.profileTypes.length > 0 ||
  filters.profileStatuses.length > 0 ||
  !filters.includeEnded;

const DEPTH_OPTIONS: Option[] = [
  { label: "All levels", value: "all" },
  { label: "1 level", value: 1 },
  { label: "2 levels", value: 2 },
  { label: "3 levels", value: 3 },
  { label: "4 levels", value: 4 },
  { label: "5 levels", value: 5 },
];

/*
 * Framed as the question being asked rather than as a hierarchy.
 *
 * "Parents" and "children" were considered and not used: many relationship
 * types are officer or control roles (director, company secretary, trustee,
 * protector) where neither word parses, "parent_company" is already a
 * relationship type code that appears in the filter below, and the chart's
 * orientation can be flipped, so a positional word would sometimes be wrong.
 */
const DIRECTION_OPTIONS: Option[] = [
  { label: "All relationships", value: "both" },
  { label: "Its related parties", value: "outbound" },
  { label: "Where it is a related party", value: "inbound" },
];

const PROFILE_TYPE_OPTIONS: Option[] = [
  { label: "Individuals", value: "individual" },
  { label: "Entities", value: "entity" },
];

const MAX_RESULTS = 8;

const asArray = (value: Option | Option[]): Option[] =>
  Array.isArray(value) ? value : value ? [value] : [];

/** how many options before the list gets its own filter box */
const SEARCHABLE_THRESHOLD = 10;

type CheckboxListProps = {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (values: string[]) => void;
  emptyLabel: string;
};

/**
 * Multi-select as a checkbox list rather than a dropdown with chips.
 *
 * Chips grow the field as selections are added and push the rest of the sidebar
 * around. Checkboxes keep every option in one place and the height fixed. The
 * list caps its height and scrolls, and gains its own filter box once there are
 * enough options for scrolling alone to be unhelpful: there are 48 relationship
 * types.
 *
 * Plain inputs rather than the design-system Checkbox, which never applies its
 * isChecked prop to the input and so cannot be controlled.
 */
const CheckboxList = ({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
}: CheckboxListProps) => {
  const [term, setTerm] = useState("");
  const isSearchable = options.length > SEARCHABLE_THRESHOLD;

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(needle)
    );
  }, [options, term]);

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((current) => current !== value)
        : [...selected, value]
    );

  return (
    <div className="org-checklist">
      <div className="org-checklist-head">
        <span className="org-checklist-label">{label}</span>
        {selected.length > 0 && (
          <button
            type="button"
            className="org-checklist-clear"
            onClick={() => onChange([])}
          >
            Clear {selected.length}
          </button>
        )}
      </div>

      {isSearchable && (
        <input
          type="search"
          className="org-checklist-search"
          placeholder="Filter"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
      )}

      <div className="org-checklist-items">
        {options.length === 0 ? (
          <span className="org-checklist-empty">{emptyLabel}</span>
        ) : visible.length === 0 ? (
          <span className="org-checklist-empty">No matches</span>
        ) : (
          visible.map((option) => {
            const value = String(option.value);
            return (
              <label className="org-checklist-item" key={value}>
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggle(value)}
                />
                <span>{option.label}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

type Props = {
  /** owned by the chart, because the toggle sits on the whiteboard */
  isOpen: boolean;
  loadFilters: LoadFilters;
  onLoadFiltersChange: (filters: LoadFilters) => void;
  showFilters: ShowFilters;
  onShowFiltersChange: (filters: ShowFilters) => void;
  /** only the nodes currently visible, so search cannot offer a hidden one */
  searchableNodes: any[];
  relationshipTypeOptions: Option[];
  statusOptions: Option[];
  onSelectNode: (nodeId: string) => void;
};

export const ChartControlsSidebar = ({
  isOpen,
  loadFilters,
  onLoadFiltersChange,
  showFilters,
  onShowFiltersChange,
  searchableNodes,
  relationshipTypeOptions,
  statusOptions,
  onSelectNode,
}: Props) => {
  // works outside <ReactFlow> because the tab wraps everything in a
  // ReactFlowProvider
  const { setCenter } = useReactFlow();
  const [searchTerm, setSearchTerm] = useState("");

  const results = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return [];

    return searchableNodes
      .filter((node) => {
        const name = String(node.data?.label ?? "").toLowerCase();
        const registration = String(
          node.data?.registration_number ?? ""
        ).toLowerCase();
        return name.includes(term) || registration.includes(term);
      })
      .slice(0, MAX_RESULTS);
  }, [searchTerm, searchableNodes]);

  const focusNode = (node: any) => {
    const width = node.measured?.width ?? 330;
    const height = node.measured?.height ?? 150;
    setCenter(node.position.x + width / 2, node.position.y + height / 2, {
      zoom: 1.2,
      duration: 600,
    });
    onSelectNode(node.id);
    setSearchTerm("");
  };

  const filtersActive =
    hasActiveLoadFilters(loadFilters) || hasActiveShowFilters(showFilters);

  const clearAll = () => {
    onLoadFiltersChange(DEFAULT_LOAD_FILTERS);
    onShowFiltersChange(DEFAULT_SHOW_FILTERS);
  };

  /*
   * A sibling of the canvas rather than an overlay on it, so collapsing gives
   * the chart genuinely more room instead of merely stopping the panel
   * covering it. The parent owns the open state, because the toggle lives on
   * the whiteboard.
   *
   * The content stays mounted while collapsed (the wrapper animates to zero
   * width and clips), so a search term and the scroll position survive a
   * collapse and reopen.
   */
  return (
    <div
      className={`org-sidebar${isOpen ? "" : " is-collapsed"}`}
      aria-hidden={!isOpen}
    >
      <div className="org-sidebar-inner">
        <div className="org-panel-header">
          <span className="org-panel-title">Find and filter</span>
          {/* its presence is the signal that filters are active, so there is no
              separate badge */}
          {filtersActive && (
            <button
              type="button"
              className="org-panel-clear-inline"
              onClick={clearAll}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="org-panel-body">
          <Input
            name="orgChartSearch"
            type="search"
            placeholder="Search"
            value={searchTerm}
            onChange={(event: any) => setSearchTerm(event.target.value)}
          />

          {searchTerm.trim().length >= 2 && (
            <Box mt={1} mb={1}>
              {results.length === 0 ? (
                <Box className="org-panel-hint">No matches on this chart</Box>
              ) : (
                results.map((node) => (
                  <Flex
                    key={node.id}
                    alignItems="center"
                    gap="8"
                    className="org-panel-result"
                    onClick={() => focusNode(node)}
                  >
                    <SubjectTypeIcon
                      isEntity={node.data?.icon === "entity"}
                      size={18}
                    />
                    <Box>
                      <Box>{node.data?.label}</Box>
                      {node.data?.registration_number && (
                        <Box color="gray.50" fontSize={0}>
                          {node.data.registration_number}
                        </Box>
                      )}
                    </Box>
                  </Flex>
                ))
              )}
            </Box>
          )}

          {/* Show first: these are the instant ones, and the ones reached for
              most. Applied to the graph already loaded, so no request and
              deliberately no reflow of the layout. */}
          <div className="org-panel-section">
            <span className="org-panel-section-title">Show</span>
            <span className="org-panel-section-hint">instant</span>
          </div>

          <CheckboxList
            label="Profile type"
            options={PROFILE_TYPE_OPTIONS}
            selected={showFilters.profileTypes}
            emptyLabel="No profile types"
            onChange={(values) =>
              onShowFiltersChange({ ...showFilters, profileTypes: values })
            }
          />

          <CheckboxList
            label="Profile status"
            options={statusOptions}
            selected={showFilters.profileStatuses}
            emptyLabel="No statuses on this chart"
            onChange={(values) =>
              onShowFiltersChange({ ...showFilters, profileStatuses: values })
            }
          />

          <Flex alignItems="center" justifyContent="space-between" mt={1}>
            <Box fontSize={0}>Include ended relationships</Box>
            <Toggle
              isChecked={showFilters.includeEnded}
              onChange={(event: any) =>
                onShowFiltersChange({
                  ...showFilters,
                  includeEnded: event.target.checked,
                })
              }
            />
          </Flex>

          {/* Load: asks the server for a different graph, so it refetches */}
          <div className="org-panel-section">
            <span className="org-panel-section-title">Load</span>
            <span className="org-panel-section-hint">refetches</span>
          </div>

          <Select
            label="Direction"
            isInModal
            options={DIRECTION_OPTIONS}
            value={
              DIRECTION_OPTIONS.find(
                (option) => option.value === loadFilters.direction
              ) ?? null
            }
            onChange={(value) => {
              const option = asArray(value)[0];
              onLoadFiltersChange({
                ...loadFilters,
                direction: (option?.value as GraphDirection) ?? "both",
              });
            }}
          />

          <Select
            label="Depth"
            isInModal
            options={DEPTH_OPTIONS}
            value={
              DEPTH_OPTIONS.find((option) =>
                loadFilters.depth === null
                  ? option.value === "all"
                  : option.value === loadFilters.depth
              ) ?? null
            }
            onChange={(value) => {
              const option = asArray(value)[0];
              onLoadFiltersChange({
                ...loadFilters,
                depth:
                  !option || option.value === "all"
                    ? null
                    : Number(option.value),
              });
            }}
          />

          <CheckboxList
            label="Relationship type"
            options={relationshipTypeOptions}
            selected={loadFilters.relationshipTypes}
            emptyLabel="No relationship types configured"
            onChange={(values) =>
              onLoadFiltersChange({
                ...loadFilters,
                relationshipTypes: values,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};
